import { CELL_SIZE, COLS, ROWS } from '../game/constants';
import { TileType, type LevelDefinition, type ObstacleDensity, type Vec2 } from '../game/types';
import { seededRandom } from '../game/utils';

const BASE_COL = 9;
const BASE_ROW = 13;
const MODULE_ANCHORS: Vec2[] = [
  { x: 1, y: 2 }, { x: 6, y: 2 }, { x: 13, y: 2 },
  { x: 2, y: 5 }, { x: 8, y: 5 }, { x: 14, y: 5 },
  { x: 1, y: 8 }, { x: 6, y: 8 }, { x: 13, y: 8 },
  { x: 4, y: 10 }, { x: 12, y: 10 },
];

const CLASSIC_MODULES = [
  ['BBBB', 'B..B'],
  ['B.B', 'BBB', 'B.B'],
  ['BB', 'BB', '..', 'BB'],
  ['R.R', 'BBB'],
  ['MM', 'BB', 'BB'],
  ['WWWW'],
  ['B..B', 'BBBB'],
  ['B', 'B', 'B', 'B'],
] as const;

const DENSITY_SETTINGS: Record<ObstacleDensity, { moduleBonus: number; scatterMultiplier: number }> = {
  low: { moduleBonus: -2, scatterMultiplier: 0.62 },
  normal: { moduleBonus: 0, scatterMultiplier: 1 },
  high: { moduleBonus: 2, scatterMultiplier: 1.34 },
  dense: { moduleBonus: 4, scatterMultiplier: 1.72 },
};

export function createEmptyMap(): TileType[][] {
  return Array.from({ length: ROWS }, () => new Array<TileType>(COLS).fill(TileType.Empty));
}

export function cloneMap(map: TileType[][]): TileType[][] {
  return map.map((row) => [...row]);
}

export function cellCenter(col: number, row: number): Vec2 {
  return {
    x: col * CELL_SIZE + CELL_SIZE / 2,
    y: row * CELL_SIZE + CELL_SIZE / 2,
  };
}

export function placeBase(map: TileType[][]): void {
  for (let row = BASE_ROW; row <= BASE_ROW + 1; row += 1) {
    for (let col = BASE_COL; col <= BASE_COL + 1; col += 1) {
      if (inside(row, col)) map[row][col] = TileType.BaseCore;
    }
  }
  for (let row = BASE_ROW - 1; row <= BASE_ROW + 1; row += 1) {
    for (let col = BASE_COL - 1; col <= BASE_COL + 2; col += 1) {
      if (inside(row, col) && map[row][col] === TileType.Empty) map[row][col] = TileType.BaseWall;
    }
  }
}

export function getLevelDefinition(level: number, density: ObstacleDensity = 'high'): LevelDefinition {
  const index = Math.max(1, level);
  const normalizedDensity = normalizeDensity(density);
  const map = index <= 15 ? generatePreset(index, normalizedDensity) : generateRandomMap(index, normalizedDensity);
  const totalEnemies = Math.min(6 + index * 2 + (index % 5 === 0 ? 1 : 0), 28);
  return {
    index,
    map,
    totalEnemies,
    maxEnemiesOnScreen: Math.min(4 + Math.floor(index / 2), 8),
    spawnInterval: Math.max(0.82, 2.45 - index * 0.08),
    spawnPoints: [cellCenter(1, 0), cellCenter(10, 0), cellCenter(18, 0), cellCenter(0, 2), cellCenter(19, 2)],
    playerSpawns: [cellCenter(3, ROWS - 2), cellCenter(COLS - 4, ROWS - 2)],
  };
}

export function isWalkableCell(map: TileType[][], row: number, col: number): boolean {
  if (!inside(row, col)) return false;
  const tile = map[row][col];
  return tile === TileType.Empty;
}

function generatePreset(level: number, density: ObstacleDensity): TileType[][] {
  const map = createEmptyMap();
  const random = seededRandom(9901 + level * 137);
  const densitySettings = DENSITY_SETTINGS[density];
  const primaryRows = level % 2 === 0 ? [2, 5, 8, 11] : [3, 6, 9];
  const gapA = 4 + (level % 4);
  const gapB = 12 + (level % 5);

  for (const row of primaryRows) {
    for (let col = 1; col < COLS - 1; col += 1) {
      if (Math.abs(col - gapA) <= 1 || Math.abs(col - gapB) <= 1 || col === 9 || col === 10) continue;
      if (random() < scaledChance(0.82, densitySettings.scatterMultiplier)) setCell(map, col, row, TileType.Brick);
    }
  }

  for (let row = 2; row <= 10; row += 2) {
    const left = 2 + ((row + level) % 4);
    const right = COLS - 3 - ((row * 2 + level) % 4);
    if (random() < scaledChance(0.8, densitySettings.scatterMultiplier)) setCell(map, left, row, TileType.Brick);
    if (random() < scaledChance(0.8, densitySettings.scatterMultiplier)) setCell(map, right, row, TileType.Brick);
    if (level > 4 && row % 4 === 0) {
      setCell(map, left + 1, row, level > 7 && random() > 0.45 ? TileType.ReinforcedWall : TileType.Metal);
      setCell(map, right - 1, row, level > 7 && random() > 0.45 ? TileType.ReinforcedWall : TileType.Metal);
    }
  }

  if (level >= 3) {
    const waterRow = 4 + (level % 5);
    for (let col = 7; col <= 12; col += 1) {
      if (col === 9 || col === 10 || random() < 0.35) continue;
      setCell(map, col, waterRow, TileType.Water);
    }
  }

  if (level >= 8) {
    for (const col of [0, 19, 5, 14]) {
      for (let row = 4; row <= 7; row += 1) {
        if (random() > 0.45) setCell(map, col, row, random() > 0.55 ? TileType.ReinforcedWall : TileType.Metal);
      }
    }
  }

  if (level % 5 === 0) {
    fillRect(map, 7, 2, 12, 3, level >= 10 ? TileType.ReinforcedWall : TileType.Metal);
    fillRect(map, 8, 5, 11, 5, TileType.Water);
    fillRect(map, 3, 10, 16, 10, TileType.Brick);
  }

  applyClassicModules(map, level, random, densityAdjustedModuleCount(2 + Math.min(4, Math.floor(level / 4)), density));
  addDensityScatter(map, level, random, density);
  clearCriticalLanes(map);
  placeBase(map);
  return map;
}

function generateRandomMap(level: number, density: ObstacleDensity): TileType[][] {
  const map = createEmptyMap();
  const random = seededRandom(level * 5519 + 41);
  const densitySettings = DENSITY_SETTINGS[density];
  applyClassicModules(map, level, random, densityAdjustedModuleCount(9 + Math.min(5, Math.floor(level / 4)), density));
  for (let row = 1; row < ROWS - 2; row += 1) {
    for (let col = 1; col < COLS - 1; col += 1) {
      if (isProtectedArea(row, col)) continue;
      const roll = random();
      if (map[row][col] !== TileType.Empty) continue;
      const brickLimit = (0.045 + Math.min(0.055, level * 0.002)) * densitySettings.scatterMultiplier;
      const reinforcedLimit = brickLimit + (0.007 + Math.min(0.035, level * 0.0012)) * densitySettings.scatterMultiplier;
      const metalLimit = reinforcedLimit + (0.012 + Math.min(0.032, level * 0.001)) * densitySettings.scatterMultiplier;
      const waterLimit = metalLimit + 0.014;
      if (roll < brickLimit) map[row][col] = TileType.Brick;
      else if (roll < reinforcedLimit) map[row][col] = TileType.ReinforcedWall;
      else if (roll < metalLimit) map[row][col] = TileType.Metal;
      else if (roll < waterLimit) map[row][col] = TileType.Water;
    }
  }
  clearCriticalLanes(map);
  placeBase(map);
  return map;
}

function densityAdjustedModuleCount(baseCount: number, density: ObstacleDensity): number {
  return Math.max(0, Math.min(MODULE_ANCHORS.length, baseCount + DENSITY_SETTINGS[density].moduleBonus));
}

function addDensityScatter(map: TileType[][], level: number, random: () => number, density: ObstacleDensity): void {
  const multiplier = DENSITY_SETTINGS[density].scatterMultiplier;
  const extraChance = Math.max(0, multiplier - 1) * 0.034;
  if (extraChance <= 0) return;
  for (let row = 1; row < ROWS - 3; row += 1) {
    for (let col = 1; col < COLS - 1; col += 1) {
      if (map[row][col] !== TileType.Empty || isProtectedArea(row, col)) continue;
      if (random() > extraChance) continue;
      const roll = random();
      if (roll < 0.72) map[row][col] = TileType.Brick;
      else if (roll < 0.88 && level >= 5) map[row][col] = TileType.ReinforcedWall;
      else if (roll < 0.96 && level >= 4) map[row][col] = TileType.Metal;
      else if (level >= 3) map[row][col] = TileType.Water;
    }
  }
}

function normalizeDensity(density: ObstacleDensity): ObstacleDensity {
  return density === 'low' || density === 'normal' || density === 'dense' ? density : 'high';
}

function scaledChance(baseChance: number, multiplier: number): number {
  return Math.max(0, Math.min(0.94, baseChance * multiplier));
}

function applyClassicModules(map: TileType[][], level: number, random: () => number, count: number): void {
  const anchors = [...MODULE_ANCHORS];
  for (let i = anchors.length - 1; i > 0; i -= 1) {
    const swap = Math.floor(random() * (i + 1));
    [anchors[i], anchors[swap]] = [anchors[swap], anchors[i]];
  }
  for (let index = 0; index < Math.min(count, anchors.length); index += 1) {
    const anchor = anchors[index];
    const module = CLASSIC_MODULES[Math.floor(random() * CLASSIC_MODULES.length)];
    const mirror = random() > 0.5;
    stampModule(map, module, anchor.x, anchor.y, level, mirror);
  }
}

function stampModule(map: TileType[][], module: readonly string[], anchorCol: number, anchorRow: number, level: number, mirror: boolean): void {
  for (let row = 0; row < module.length; row += 1) {
    const line = module[row];
    for (let offset = 0; offset < line.length; offset += 1) {
      const sourceCol = mirror ? line.length - 1 - offset : offset;
      const tile = tileFromModuleChar(line[sourceCol], level);
      if (tile === TileType.Empty) continue;
      const col = anchorCol + offset;
      const targetRow = anchorRow + row;
      if (isProtectedArea(targetRow, col)) continue;
      setCell(map, col, targetRow, tile);
    }
  }
}

function tileFromModuleChar(char: string, level: number): TileType {
  if (char === 'B') return TileType.Brick;
  if (char === 'R') return level >= 6 ? TileType.ReinforcedWall : TileType.Brick;
  if (char === 'M') return level >= 5 ? TileType.Metal : TileType.Brick;
  if (char === 'W') return level >= 3 ? TileType.Water : TileType.Empty;
  return TileType.Empty;
}

function clearCriticalLanes(map: TileType[][]): void {
  const clearCells = [
    [1, 0], [10, 0], [18, 0], [0, 2], [19, 2],
    [3, 13], [16, 13], [3, 12], [16, 12],
    [9, 11], [10, 11], [9, 12], [10, 12],
  ];
  for (const [col, row] of clearCells) setCell(map, col, row, TileType.Empty, true);

  for (let row = 0; row < ROWS; row += 1) {
    if (row < 11) {
      setCell(map, 3, row, TileType.Empty, true);
      setCell(map, 10, row, TileType.Empty, true);
      setCell(map, 16, row, TileType.Empty, true);
    }
  }
  for (let col = 0; col < COLS; col += 1) {
    if (col < 8 || col > 11) setCell(map, col, 12, TileType.Empty, true);
  }
}

function isProtectedArea(row: number, col: number): boolean {
  if (row <= 2 && (col <= 2 || col >= 17 || (col >= 9 && col <= 11))) return true;
  if (row >= 11 && col >= 7 && col <= 12) return true;
  if (row >= 12 && (col === 3 || col === 16)) return true;
  return false;
}

function setCell(map: TileType[][], col: number, row: number, tile: TileType, force = false): void {
  if (!inside(row, col)) return;
  if (force || map[row][col] === TileType.Empty) map[row][col] = tile;
}

function fillRect(map: TileType[][], c1: number, r1: number, c2: number, r2: number, tile: TileType): void {
  for (let row = r1; row <= r2; row += 1) {
    for (let col = c1; col <= c2; col += 1) {
      setCell(map, col, row, tile);
    }
  }
}

function inside(row: number, col: number): boolean {
  return row >= 0 && row < ROWS && col >= 0 && col < COLS;
}
